import { NgModule, Injector, NgZone, inject, provideAppInitializer } from '@angular/core';
import { RouterModule, Routes, Router } from '@angular/router';
import { AdminRolesEnum } from '@shared/models';
import {
  AuthGuard,
  UserResolver,
  AdminGuard,
  AdminSiteGuard,
  DemoUserResolver,
  CompanyAdminGuard,
  LoadScriptResolver,
  DemoProgramService,
} from '@shared/services';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: ({ queryParams }) => inject(Router).createUrlTree(['/auth/login'], { queryParams }),
  },
  {
    path: 'auth',
    loadChildren: () =>
      import('./features/authentication/authentication.module').then((m) => m.AuthenticationModule),
    canActivate: [AuthGuard],
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'captcha',
    loadChildren: () => import('./features/captcha/captcha.module').then((m) => m.CaptchaModule),
  },
  {
    path: 'home',
    loadChildren: () => import('./features/programs/programs.module').then((m) => m.ProgramsModule),
    resolve: { data: UserResolver },
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'profile',
    loadChildren: () => import('./features/profile/profile.module').then((m) => m.ProfileModule),
    resolve: { data: UserResolver },
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'certificates',
    loadChildren: () => import('./features/home/home.module').then((m) => m.HomeModule),
    resolve: { data: UserResolver },
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'support',
    loadChildren: () => import('./features/support/support.module').then((m) => m.SupportModule),
    resolve: { data: UserResolver },
    data: {
      fullscreen: false,
    },
  },

  {
    path: 'faq',
    loadChildren: () => import('./features/faq/faq.module').then((m) => m.FaqModule),
    resolve: { data: UserResolver },
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'resources',
    loadChildren: () =>
      import('./features/resources/resources.module').then((m) => m.ResourcesModule),
    resolve: { data: UserResolver },
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'search_food',
    loadChildren: () =>
      import('./features/search-food/search-food.module').then((m) => m.SearchFoodModule),
    resolve: { data: UserResolver },
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'welcome',
    loadChildren: () => import('./features/welcome/welcome.module').then((m) => m.WelcomeModule),
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'program',
    loadChildren: () => import('./features/program/program.module').then((m) => m.ProgramModule),
    resolve: { data: UserResolver },
    pathMatch: 'prefix',
    data: {
      fullscreen: true,
    },
  },
  {
    path: 'testmodeprogram',
    loadChildren: () => import('./features/program/program.module').then((m) => m.ProgramModule),
    data: {
      fullscreen: true,
      isTestMode: true,
    },
  },
  {
    path: 'simulator',
    loadChildren: () => import('./features/case/case.module').then((m) => m.CaseModule),
    resolve: { data: UserResolver },
    data: {
      fullscreen: true,
    },
  },
  {
    path: 'casedemo',
    loadChildren: () => import('./features/case/case.module').then((m) => m.CaseModule),
    data: {
      fullscreen: true,
      isTestMode: true,
    },
  },
  {
    path: 'purchase',
    loadChildren: () => import('./features/purchase/purchase.module').then((m) => m.PurchaseModule),
    resolve: { isLoad: LoadScriptResolver, user: UserResolver },
    data: {
      fullscreen: false,
    },
  },
  {
    path: DemoProgramService.programPath,
    loadChildren: () => import('./features/program/program.module').then((m) => m.ProgramModule),
    pathMatch: 'prefix',
    resolve: { data: DemoUserResolver },
    data: {
      fullscreen: true,
      program: 'demo',
    },
  },
  {
    path: DemoProgramService.simulatorPath,
    loadChildren: () => import('./features/case/case.module').then((m) => m.CaseModule),
    resolve: { data: DemoUserResolver },
    data: {
      fullscreen: true,
      program: 'demo',
    },
  },
  {
    path: 'admin',
    loadChildren: () => import('./features/admin/admin.module').then((m) => m.AdminModule),
    canActivate: [AdminGuard],
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'company_admin',
    loadChildren: () =>
      import('./features/company-admin/company-admin.module').then((m) => m.CompanyAdminModule),
    canActivate: [CompanyAdminGuard],
    data: {
      role: AdminRolesEnum.CompanyAdmin,
    },
  },
  {
    path: 'company_groups',
    loadChildren: () =>
      import('./features/company-groups/company-groups.module').then((m) => m.CompanyGroupsModule),
    canActivate: [AdminGuard],
  },
  {
    path: 'corporation_admin',
    loadChildren: () =>
      import('./features/corporation-admin/corporation-admin.module').then(
        (m) => m.CorporationAdminModule,
      ),
    canActivate: [CompanyAdminGuard],
    data: {
      role: AdminRolesEnum.CorporationAdmin,
    },
  },
  {
    path: 'group_admin',
    loadChildren: () =>
      import('./features/group-admin/group-admin.module').then((m) => m.GroupAdminModule),
    canActivate: [CompanyAdminGuard],
    data: {
      role: AdminRolesEnum.CompanyGroupsAdmin,
    },
  },
  {
    path: 'admin-vivenhealth',
    loadChildren: () =>
      import('./features/admin-vivenhealth/admin-vivenhealth.module').then(
        (m) => m.AdminVivenHealthModule,
      ),
    canActivate: [AdminSiteGuard],
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'certificate',
    loadChildren: () =>
      import('./features/certificate/certificate.module').then((m) => m.CertificateModule),
    resolve: { data: UserResolver },
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'reward',
    loadChildren: () => import('./features/reward/reward.module').then((m) => m.RewardModule),
    data: {
      fullscreen: false,
    },
  },
  {
    path: 'preview',
    loadChildren: () => import('./features/case/case.module').then((m) => m.CaseModule),
    outlet: 'popup',
    data: {
      fullscreen: true,
    },
  },
  {
    path: 'program-popup',
    loadChildren: () => import('./features/case/case.module').then((m) => m.CaseModule),
    outlet: 'program-popup',
    data: {
      fullscreen: true,
    },
  },
  {
    path: 'sales-page',
    loadChildren: () =>
      import('./features/sales-page/sales-page.module').then((m) => m.SalesPagedModule),
    outlet: 'popup',
    data: {
      popup: true,
      fullscreen: true,
    },
  },
  {
    path: 'shop',
    loadChildren: () =>
      import('./features/sales-page/sales-page.module').then((m) => m.SalesPagedModule),
    resolve: { data: DemoUserResolver },
    data: {
      popup: false,
      fullscreen: false,
    },
  },
  // {
  // 	path: 'marketplace',
  // 	loadChildren: () => import('./features/marketplace/marketplace.module').then(m => m.MarketplaceModule),
  // 	resolve: { data: UserResolver },
  // 	data: {
  // 		popup: false,
  // 		fullscreen: false,
  // 	}
  // },
  {
    path: 'app-view-guide',
    loadChildren: () =>
      import('./features/full-screen-guide/full-screen-guide.module').then(
        (m) => m.FullScreenGuideModule,
      ),
    data: {
      popup: false,
      fullscreen: false,
    },
  },
  {
    path: '**',
    pathMatch: 'full',
    redirectTo: ({ queryParams }) => inject(Router).createUrlTree(['/auth/login'], { queryParams }),
  },
];

const miniProgramRoutes: Routes = [
  {
    path: '',
    redirectTo: DemoProgramService.programURL,
    pathMatch: 'full',
  },
  {
    path: DemoProgramService.programPath,
    loadChildren: () => import('./features/program/program.module').then((m) => m.ProgramModule),
    pathMatch: 'prefix',
    data: {
      fullscreen: true,
      program: 'demo',
    },
  },
  {
    path: DemoProgramService.simulatorPath,
    loadChildren: () => import('./features/case/case.module').then((m) => m.CaseModule),
    data: {
      fullscreen: true,
      program: 'demo',
    },
  },
  {
    path: 'certificate',
    loadChildren: () =>
      import('./features/certificate/certificate.module').then((m) => m.CertificateModule),
    data: {
      fullscreen: false,
    },
  },
  {
    path: '**',
    redirectTo: DemoProgramService.programURL,
    pathMatch: 'full',
  },
];

export function routerConfigFactory(injector: Injector): Function {
  return () => {
    const router: Router = injector.get(Router);
    const zone: NgZone = injector.get(NgZone);
    const demoprogramService: DemoProgramService = injector.get(DemoProgramService);
    demoprogramService
      .checkIfDemo()
      .then((res) => {
        if (res) {
          router.resetConfig(miniProgramRoutes);
          setTimeout(() => {
            zone.run(() => router.navigate([DemoProgramService.programURL]));
          }, 0);
        }
      })
      .catch((e) => console.log(e));
  };
}

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = routerConfigFactory(inject(Injector));
      return initializerFn();
    }),
  ],
})
export class MainRoutingModule {}
